import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    makeStyles,
    Tooltip,
    Typography,
    useTheme
} from "@material-ui/core";
import * as yup from "yup";
import {CreateClientData, FeClient} from "../redux/types";
import React, {FC} from "react";
import {useHistory} from "react-router-dom";
import {useEditorLocation} from "../../../editor/editor.hook";
import {useCreateClient} from "../redux/clients.hook";
import {Form, Formik} from "formik";
import {FormikTextField} from "../../../form/FormikTextField";
import {FormikSelect} from "../../../form/FormikSelect";

const useStyles = makeStyles(theme => ({
    formContainer: {
        paddingBottom: theme.spacing(4)
    }
}));

const validationSchema = yup.object({
    salutation: yup.string().required('required'),
    firstName: yup.string().required('required'),
    lastName: yup.string().required('required'),
    companyName: yup.string().notRequired(),
    email: yup.string().email().required()
});

const initialValues: CreateClientData = {
    firstName: '',
    lastName: '',
    email: '',
    companyName: undefined,
    salutation: 'Herr'
};

interface CreateClientDialogFormProps {
    withoutButton?: boolean;
    onCreated?: (clientId: string) => void;
    open?: boolean;
    onClose?: () => void;
}

const GenerateFakeEmailButton: FC<{ onClick: (email: string) => void, firstName: string, lastName: string }> = ({
                                                                                                                    onClick,
                                                                                                                    firstName,
                                                                                                                    lastName
                                                                                                                }) => {
    const theme = useTheme();

    const generateFakeEmail = (firstName: string, lastName: string) => {
        return `${firstName}.${lastName}-${new Date().getTime()}@diefleissigenenkel.de`
    }

    return (
        <Tooltip
            title={"Bitte nur im Notfall nutzen. Diese Email wird nicht existieren. Es werden keine Emails an diesen Kunden verschickt"}>
            <Button
                style={{whiteSpace: "nowrap", color: theme.palette.warning.dark}}
                size="small"
                onClick={() => onClick(generateFakeEmail(firstName, lastName))}
            >
                Notfall Email
            </Button>
        </Tooltip>
    )


}

export const CreateClientDialogForm: FC<CreateClientDialogFormProps> = ({withoutButton, onCreated, open, onClose}) => {

    const classes = useStyles();
    const history = useHistory();
    const {mode, reference} = useEditorLocation();
    const {createClient} = useCreateClient();

    const [isLoading, setIsLoading] = React.useState(false);

    const dialogOpen = open ?? (mode === 'create' && reference === 'client');

    const openDialog = () => history.replace(`${history.location.pathname}?mode=create#client`);
    const closeDialog = () => onClose ? onClose() : history.replace({search: undefined});

    return (
        <>
            {!withoutButton && (
                <Button
                    variant="contained"
                    color="primary"
                    onClick={openDialog}
                >
                    Neu
                </Button>)}
            <Dialog open={dialogOpen} onClose={closeDialog}>
                <DialogTitle>
                    <Typography variant="inherit" color="primary">
                        Neuer Kunde
                    </Typography>
                </DialogTitle>
                <Formik
                    initialValues={initialValues}
                    onSubmit={(dto) => {
                        setIsLoading(true);
                        createClient(dto)
                            .then((res) => {
                                if(onCreated) {
                                    onCreated((res.payload as FeClient).id);
                                    closeDialog();
                                }else {
                                    closeDialog();
                                }
                            })
                            .catch(() => {
                            })
                            .finally(() => {
                                setIsLoading(false)
                            })
                    }}
                    validationSchema={validationSchema}
                >
                    {({submitForm, resetForm, setFieldValue, values}) => (
                        <Form>
                            <DialogContent className={classes.formContainer}>
                                <Grid container spacing={2}>
                                    <Grid item xs={2}>
                                        <FormikSelect
                                            fullWidth
                                            name={"salutation"}
                                            label="Anrede"
                                            required
                                            options={[
                                                {value: 'Herr', label: 'Herr'},
                                                {value: 'Frau', label: 'Frau'},
                                                {value: 'Divers', label: 'Divers'},
                                            ]}
                                        />
                                    </Grid>
                                    <Grid item xs={5}>
                                        <FormikTextField fullWidth name={"firstName"} label="Vorname" required/>
                                    </Grid>
                                    <Grid item xs={5}>
                                        <FormikTextField fullWidth name={"lastName"} label="Nachname" required/>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <FormikTextField
                                            fullWidth
                                            name={"email"}
                                            label="Email"
                                            type="email"
                                            required
                                            InputProps={{
                                                endAdornment:
                                                    <GenerateFakeEmailButton
                                                        onClick={(generatedEmail) => setFieldValue("email", generatedEmail)}
                                                        firstName={values.firstName}
                                                        lastName={values.lastName}
                                                    />
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <FormikTextField fullWidth name={"companyName"} label="Firma"/>
                                    </Grid>
                                </Grid>
                            </DialogContent>
                            <DialogActions>
                                {isLoading ?
                                    <CircularProgress/> :
                                    (<>
                                        <Button
                                            color="primary"
                                            onClick={() => {
                                                closeDialog();
                                                resetForm();
                                            }}>
                                            Cancel
                                        </Button>
                                        <Button color="primary" onClick={submitForm}>
                                            Save
                                        </Button>
                                    </>)}
                            </DialogActions>
                        </Form>
                    )}
                </Formik>
            </Dialog>
        </>
    )
}
